// ФТ-3.7. Фото с телефона: сжатие до ~1 МБ прямо на телефоне и очередь отправки.
// Фото лежат в IndexedDB (в localStorage они не помещаются) и уходят в фоне, по одному;
// без связи ждут сети, после перезапуска браузера тоже. Каждое фото несёт свой Idempotency-Key —
// повторная отправка после обрыва не создаёт дубля на сервере.

import { Api, ApiError, uid } from "./api";

export const TARGET_BYTES = 1_000_000;
const MAX_SIDE = 2048;

/** Уменьшить фото до 2048 px по длинной стороне и подобрать качество JPEG, чтобы уложиться в ~1 МБ. */
export async function compressImage(file: Blob): Promise<Blob> {
  let bmp: ImageBitmap;
  try {
    // поворот по EXIF (телефон снимает «боком») браузер применяет сам
    bmp = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
  } catch {
    return file; // формат браузер не открыл — отправим как есть, сервер пережмёт сам
  }
  const k = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
  if (k === 1 && file.size <= TARGET_BYTES && file.type === "image/jpeg") { bmp.close(); return file; }
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bmp.width * k);
  canvas.height = Math.round(bmp.height * k);
  const ctx = canvas.getContext("2d");
  if (!ctx) { bmp.close(); return file; }
  ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  bmp.close();
  let best: Blob | null = null;
  for (const q of [0.85, 0.75, 0.65, 0.55, 0.45]) {
    const b = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, "image/jpeg", q));
    if (!b) break;
    best = b;
    if (b.size <= TARGET_BYTES) break;
  }
  return best && best.size < file.size ? best : file;
}

export type PhotoMeta = {
  task_id?: string; issue_id?: string; report_id?: string;
  report_date?: string; report_shift?: string;
  floor_number?: string; grip_code?: string; caption?: string;
};

export type QueuedPhoto = {
  id: string; key: string; buildingId: string; meta: PhotoMeta; blob: Blob; name: string;
  takenAt: string; createdAt: string; error?: string;
};

type Listener = (items: QueuedPhoto[]) => void;
const DB = "rbs-photos";
const STORE = "queue";

function openDb(): Promise<IDBDatabase> {
  return new Promise((ok, fail) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: "id" });
    r.onsuccess = () => ok(r.result);
    r.onerror = () => fail(r.error);
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then((db) => new Promise<T>((ok, fail) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => { ok(req.result); db.close(); };
    t.onerror = () => { fail(t.error); db.close(); };
  }));
}

export class PhotoQueue {
  private listeners = new Set<Listener>();
  private running = false;
  private memory: QueuedPhoto[] = []; // если IndexedDB недоступна (приватный режим) — очередь живёт до закрытия вкладки
  private persistent = typeof indexedDB !== "undefined";
  /** Что выполнить перед отправкой фото: сначала уходит очередь отчётов, чтобы фото нашли свой отчёт. */
  before: () => Promise<void> = async () => {};

  constructor(private api: Api) {
    window.addEventListener("online", () => void this.flush());
    setInterval(() => void this.flush(), 30_000);
    setTimeout(() => void this.flush(), 2_000);
  }

  async items(): Promise<QueuedPhoto[]> {
    if (!this.persistent) return this.memory;
    try {
      const all = await tx<QueuedPhoto[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedPhoto[]>);
      return all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    } catch {
      this.persistent = false;
      return this.memory;
    }
  }

  private async put(p: QueuedPhoto): Promise<void> {
    if (this.persistent) {
      try { await tx("readwrite", (s) => s.put(p)); return; } catch { this.persistent = false; }
    }
    this.memory = [...this.memory.filter((x) => x.id !== p.id), p];
  }

  async remove(id: string): Promise<void> {
    if (this.persistent) {
      try { await tx("readwrite", (s) => s.delete(id)); } catch { /* уже удалено */ }
    }
    this.memory = this.memory.filter((x) => x.id !== id);
    await this.notify();
  }

  private async notify(): Promise<void> {
    const items = await this.items();
    this.listeners.forEach((l) => l(items));
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    void this.items().then(l);
    return () => this.listeners.delete(l);
  }

  /** Сжать и поставить в очередь. Отправка начинается сразу в фоне — ждать её не нужно. */
  async add(buildingId: string, file: File, meta: PhotoMeta): Promise<void> {
    const blob = await compressImage(file);
    const taken = file.lastModified ? new Date(file.lastModified) : new Date();
    await this.put({
      id: uid(), key: uid(), buildingId, meta, blob, name: file.name || "photo.jpg",
      takenAt: taken.toISOString(), createdAt: new Date().toISOString(),
    });
    await this.notify();
    void this.flush();
  }

  async flush(): Promise<void> {
    if (this.running || !navigator.onLine) return;
    this.running = true;
    try {
      await this.before().catch(() => {});
      for (const p of await this.items()) {
        if (p.error) continue;
        const fd = new FormData();
        fd.append("file", p.blob, p.name.replace(/\.[^.]+$/, "") + ".jpg");
        fd.append("taken_at", p.takenAt);
        for (const [k, v] of Object.entries(p.meta)) if (v) fd.append(k, v);
        try {
          await this.api.raw("POST", `/buildings/${p.buildingId}/photos`, fd, true, { "Idempotency-Key": p.key });
          await this.remove(p.id);
        } catch (e) {
          if (e instanceof ApiError && (e.status === 0 || e.status >= 500 || e.status === 401)) break; // связь или сервер — повторим позже
          await this.put({ ...p, error: (e as Error).message });
          await this.notify();
        }
      }
    } finally {
      this.running = false;
    }
  }
}
